import React from 'react';
import { Grid, IconButton, MenuItem, TextField } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { css } from '@emotion/react';
import { DebouncedTextField, OnwardSwitch } from '@/styles/blocks';

const CarrierMappingRow = ({ mapping, carriersForLeg, collapsed, loading, callbacks, isOnwardAdmin, onClearNew }) => {
    const carrierOptions = carriersForLeg('carrier_id');
    const warehouseCarrierOptions = carriersForLeg('warehouse_carrier_id');
    const mmCarrierOptions = carriersForLeg('middle_mile_carrier_id');

    const handleAfterUpsert = () => {
        if (!mapping.mapping_id) onClearNew?.();
    };

    return (
        <>
            <Grid
                container
                css={css`
                    padding: 12px 0px 6px 12px;
                    flex-wrap: nowrap;
                `}
            >
                <Grid
                    item
                    css={css`
                        flex-grow: 1;
                    `}
                >
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        select
                        id={`carrier-select-${mapping.mapping_id}`}
                        label={`Carrier ${mapping.is_primary ? '(Primary)' : '(Additional)'}`}
                        value={mapping.carrier_id || ''}
                        onChange={(e) =>
                            callbacks
                                .addCarrierMapping({
                                    ...mapping,
                                    carrier_id: e.target.value,
                                    warehouse_id: null,
                                })
                                .then(handleAfterUpsert)
                        }
                        disabled={loading.carriers}
                    >
                        {carrierOptions.map((c) => (
                            <MenuItem key={c.client_id} value={c.client_id}>
                                {c.business_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item>
                    <IconButton
                        onClick={() =>
                            callbacks.setCollapsed((prev) => ({
                                ...prev,
                                [mapping.mapping_id]: !prev[mapping.mapping_id],
                            }))
                        }
                    >
                        {collapsed[mapping.mapping_id] ? <ExpandMore /> : <ExpandLess />}
                    </IconButton>
                </Grid>
            </Grid>

            {mapping.carrier_id && !collapsed[mapping.mapping_id] ? (
                <>
                    <Grid
                        container
                        css={css`
                            padding: 6px 12px;
                            flex-wrap: nowrap;
                        `}
                    >
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                select
                                label="WH Carrier"
                                value={mapping.warehouse_carrier_id || ''}
                                onChange={(e) =>
                                    callbacks
                                        .addCarrierMapping({
                                            ...mapping,
                                            warehouse_carrier_id: e.target.value,
                                            warehouse_id: null,
                                        })
                                        .then(handleAfterUpsert)
                                }
                                disabled={loading.carriers}
                            >
                                {warehouseCarrierOptions.map((c) => (
                                    <MenuItem key={c.client_id} value={c.client_id}>
                                        {c.business_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                select
                                label="Receiving WH"
                                value={mapping.warehouse_id || ''}
                                onChange={(e) =>
                                    callbacks.addCarrierMapping({ ...mapping, warehouse_id: e.target.value })
                                }
                                disabled={loading.carriers}
                            >
                                {(
                                    (warehouseCarrierOptions.find(
                                        (c) => c.client_id === (mapping.warehouse_carrier_id || mapping.carrier_id)
                                    ) || carrierOptions.find(
                                        (c) => c.client_id === (mapping.carrier_id)
                                    ))?.locations || []
                                ).map((location, i) => (
                                    <MenuItem key={location.location_id} value={location.location_id}>
                                        {location.location_name || `Warehouse ${i + 1}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                    {mmCarrierOptions.length > 0 ? (
                        <Grid
                            container
                            css={css`
                                padding: 6px 12px;
                                flex-wrap: nowrap;
                            `}
                        >
                            <Grid item xs={3}>
                                <OnwardSwitch
                                    label="Direct"
                                    checked={!!mapping.is_direct}
                                    onChange={(_, checked) =>
                                        callbacks.addCarrierMapping({
                                            ...mapping,
                                            is_direct: checked,
                                            ...(checked ? { middle_mile_carrier_id: null } : {}),
                                        })
                                    }
                                    disabled={loading.carriers}
                                />
                            </Grid>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    select
                                    label="MM Carrier"
                                    value={mapping.middle_mile_carrier_id || ''}
                                    onChange={(e) =>
                                        callbacks
                                            .addCarrierMapping({ ...mapping, middle_mile_carrier_id: e.target.value })
                                            .then(handleAfterUpsert)
                                    }
                                    disabled={loading.carriers || mapping.is_direct}
                                >
                                    {mmCarrierOptions.map((c) => (
                                        <MenuItem key={c.client_id} value={c.client_id}>
                                            {c.business_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    ) : null}
                    {!isOnwardAdmin ? (
                        <>
                            <Grid
                                container
                                css={css`
                                    padding: 6px 12px;
                                    flex-wrap: nowrap;
                                `}
                            >
                                <Grid
                                    item
                                    xs={6}
                                    css={css`
                                        padding-right: 6px;
                                    `}
                                >
                                    <DebouncedTextField
                                        label="Site Code"
                                        disabled={loading.carriers}
                                        value={mapping.site_code || ''}
                                        onChange={(e) =>
                                            callbacks.addCarrierMapping({ ...mapping, site_code: e.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    css={css`
                                        padding-left: 6px;
                                    `}
                                >
                                    <DebouncedTextField
                                        label="Site Contact Name"
                                        disabled={loading.carriers}
                                        value={mapping.site_contact_name || ''}
                                        onChange={(e) =>
                                            callbacks.addCarrierMapping({
                                                ...mapping,
                                                site_contact_name: e.target.value,
                                            })
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                css={css`
                                    padding: 6px 12px;
                                    flex-wrap: nowrap;
                                `}
                            >
                                <Grid
                                    item
                                    xs={6}
                                    css={css`
                                        padding-right: 6px;
                                    `}
                                >
                                    <DebouncedTextField
                                        label="Site Contact Email"
                                        disabled={loading.carriers}
                                        value={mapping.site_contact_email || ''}
                                        onChange={(e) =>
                                            callbacks.addCarrierMapping({
                                                ...mapping,
                                                site_contact_email: e.target.value,
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    css={css`
                                        padding-left: 6px;
                                    `}
                                >
                                    <DebouncedTextField
                                        label="Site Contact Phone"
                                        disabled={loading.carriers}
                                        value={mapping.site_contact_phone || ''}
                                        onChange={(e) =>
                                            callbacks.addCarrierMapping({
                                                ...mapping,
                                                site_contact_phone: e.target.value,
                                            })
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </>
                    ) : null}
                </>
            ) : null}
        </>
    );
};

export default CarrierMappingRow;
